<template>
  <div class="relative bg-white p-6 rounded-lg shadow-lg w-full md:max-w-2xl dark:bg-gray-700">
    <h2 class="text-l font-bold text-gray-900 mb-4 dark:text-gray-100">
      Related Tickets
    </h2>

    <div class="space-y-4">
      <div>
        <button @click="clearSelection"
          class="mb-2 inline-flex items-center px-2 py-2 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-red-500 hover:bg-red-600">
          Do NOT Attach to Ticket
        </button>

        <div v-if="loading" class="py-10 text-center text-sm text-gray-500 dark:text-gray-300">
          Loading related tickets...
        </div>

        <div v-else-if="paginatedTickets.length === 0"
          class="py-10 text-center text-sm text-gray-500 dark:text-gray-300">
          No related tickets found.
        </div>

        <div v-else class="space-y-2">
          <div v-for="ticket in paginatedTickets" :key="ticket.id"
            class="p-4 bg-gray-100 rounded-md flex justify-between items-center dark:bg-gray-800">
            <span class="text-sm dark:text-gray-100">
              {{ ticket.submitted }} {{ ticket.id }} {{ ticket.tickettype }}:
              {{ ticket.firstname }} {{ ticket.lastname }} -
              <span class="font-bold italic">{{ ticket.requester }}</span><br />

              <template v-if="ticket.description?.includes(':')">
                {{ ticket.tickettype }}{{ ticket.description }}
              </template>
              <template v-else>
                {{ ticket.tickettype }}: {{ ticket.description }}
              </template>
            </span>

            <button @click="selectTicket(ticket)" :class="ticket.id === selectedTicketId
              ? 'bg-green-600 hover:bg-green-700'
              : 'bg-blue-500 hover:bg-blue-600'"
              class="ml-4 inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white">
              {{ ticket.id === selectedTicketId ? 'Selected' : 'Select' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="flex justify-center mt-4">
      <button v-if="hasPreviousPage" :disabled="currentPage === 1" @click="currentPage -= 1"
        class="bg-gray-600 text-gray-100 rounded-lg px-3 py-1 mr-2 hover:bg-gray-800 text-sm dark:bg-gray-200 dark:text-gray-800 dark:hover:bg-gray-300">
        Previous
      </button>

      <button v-if="hasNextPage" :disabled="currentPage * pageSize >= filteredTickets.length" @click="currentPage += 1"
        class="bg-gray-600 text-gray-100 rounded-lg px-3 py-1 hover:bg-gray-800 text-sm dark:bg-gray-200 dark:text-gray-800 dark:hover:bg-gray-300">
        Next
      </button>
    </div>

    <div class="mt-8 flex justify-end">
      <button
        class="bg-gray-600 text-gray-100 rounded-lg px-5 py-2 hover:bg-gray-800 text-sm dark:bg-gray-200 dark:text-gray-800 dark:hover:bg-gray-300"
        @click="emitClose">
        Cancel
      </button>
    </div>
  </div>
</template>

<script setup>
import { defineEmits, onMounted, ref, defineProps, computed, watch } from "vue";
import axios from "axios";

const props = defineProps({
  clientId: {
    type: Number,
    required: true,
  },
  attachTo: {
    type: Number,
    required: true
  },
  excludeTicketId: {
    type: Number,
    default: 0
  }
});

const emit = defineEmits(["close", "handleRelatedTickets"]);

const relatedTickets = ref([]);
const selectedTicketId = ref(0);
const loading = ref(false);

// Pagination
const currentPage = ref(1);
const pageSize = 6;

const emitClose = () => {
  emit("close");
};

onMounted(() => {
  fetchRelatedTickets();
});

watch(
  () => props.attachTo,
  (newVal) => {
    selectedTicketId.value = newVal || 0;
  },
  { immediate: true }
);

watch(
  () => props.clientId,
  () => {
    currentPage.value = 1;
    fetchRelatedTickets();
  }
);

async function fetchRelatedTickets() {
  loading.value = true;

  try {
    const response = await axios.get(`${axios.defaults.baseURL}ticket/related-tickets`, {
      params: {
        client_id: props.clientId,
      },
    });

    relatedTickets.value = (response.data || []).filter(
      (ticket) => Number(ticket.id) !== Number(props.excludeTicketId)
    );
  } catch (error) {
    console.error("Error fetching related tickets:", error);
    relatedTickets.value = [];
  } finally {
    loading.value = false;
  }
}

function clearSelection() {
  selectedTicketId.value = 0;

  emit("handleRelatedTickets", {
    id: 0,
    ticket: null
  });
}

function selectTicket(ticket) {
  selectedTicketId.value = ticket.id;

  emit("handleRelatedTickets", {
    id: ticket.id,
    ticket
  });
}

const filteredTickets = computed(() => relatedTickets.value);

const paginatedTickets = computed(() => {
  const startIndex = (currentPage.value - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  return filteredTickets.value.slice(startIndex, endIndex);
});

const hasPreviousPage = computed(() => currentPage.value > 1);
const hasNextPage = computed(
  () => currentPage.value * pageSize < filteredTickets.value.length
);
</script>